import { onAuthStateChanged } from "firebase/auth";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  initializeAxiosClient,
  useApiClient,
  useApiClientStatus,
} from "~/lib/api/axios-client";
import { identifyDatadogUser, initializeDatadog } from "~/lib/datadog";
import { env } from "~/lib/env";
import { setSentryUser } from "~/lib/sentry";
import { getFirebaseAuth } from "./lib/firebase";
import { type AppDispatch, handleAuthInitialization } from "./store";

/**
 * @description An invisible component responsible for global application initialization.
 * It sets up the permanent Firebase Auth listener and dispatches Redux Thunks
 * to manage the global authentication and profile loading state.
 */
function AppInitializer() {
  const dispatch = useDispatch<AppDispatch>();
  const authInstance = getFirebaseAuth();

  const { isReady } = useApiClientStatus();
  const apiInstance = useApiClient();

  useEffect(() => {
    if (!isReady || !apiInstance) return;

    initializeAxiosClient(env.VITE_API_URL).catch((error) => {
      console.error("Failed to initialize axios client:", error);
    });

    // Initialize Datadog RUM only in production environment
    if (
      env.VITE_DATADOG_APPLICATION_ID &&
      env.VITE_DATADOG_CLIENT_TOKEN &&
      env.VITE_APP_ENV === "prd"
    ) {
      initializeDatadog({
        applicationId: env.VITE_DATADOG_APPLICATION_ID,
        clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
        site: env.VITE_DATADOG_SITE || "datadoghq.com",
        service: env.VITE_DATADOG_SERVICE || "monthly-web-app",
        env: env.VITE_APP_ENV,
        version: "1.0.0", // TODO: Get from package.json or env
      });
    }
  }, [isReady, apiInstance]);

  useEffect(() => {
    if (!isReady || !apiInstance) return;

    const unsubscribe = onAuthStateChanged(authInstance, (user) => {
      // Set Sentry user context immediately when Firebase auth state changes
      setSentryUser(user);

      // Identify user in Datadog (production only)
      if (
        user &&
        env.VITE_DATADOG_APPLICATION_ID &&
        env.VITE_APP_ENV === "prd"
      ) {
        identifyDatadogUser(user.uid, {
          name: user.displayName || "unknown",
          email: user.email || "unknown",
        });
      }

      dispatch(handleAuthInitialization(user));
    });

    return () => unsubscribe();
  }, [dispatch, isReady, apiInstance, authInstance]);

  return null;
}

export default AppInitializer;
